import {Box} from 'platform/foundation';
import {useFormatCurrency} from 'platform/locale';
import {match} from 'ts-pattern';

import {isNil, isNotNil} from 'ramda';

import {
  FinancingRateMatrixCell,
  SaveSaleVehicleFinancingBody,
  VehicleFinancingType,
} from '@dms/api/vehicle';
import {isValidBalloonCombination} from '@dms/features/vehicle-financing-template';
import i18n from '@dms/i18n';

import {EMPTY_PLACEHOLDER} from 'shared/web';

import {FinancingCellCoordinates} from '../types/FinancingCellCoordinates';
import {VehicleFinancingPricing} from '../types/VehicleFinancingPricing';
import {areDecimalValuesEqual} from '../utils/areDecimalValuesEqual';
import {buildMatrixCellAccessibleName} from '../utils/buildMatrixCellAccessibleName';
import {calculateFinancingOffer} from '../utils/calculateFinancingOffer';
import {FinancingCellTooltip} from './FinancingCellTooltip';
import {FinancingMatrixCell} from './FinancingMatrixCell';

interface FinancingMatrixRowCellProps {
  vehicleFinancingBody: SaveSaleVehicleFinancingBody;
  type: VehicleFinancingType;
  term: number;
  downPayment: number;
  balloonPayment: number | null;
  cell: FinancingRateMatrixCell | null;
  templateCell: FinancingRateMatrixCell | undefined;
  vehiclePrice: number;
  currency: string;
  pricing: VehicleFinancingPricing | null;
  isTemplatePending?: boolean;
  isReadOnly: boolean;
  isSelected: boolean;
  onToggleSelection: (coordinates: FinancingCellCoordinates) => void;
  onEditCell: (cell: FinancingCellCoordinates) => void;
}

/** One term × down-payment position of the financing rate matrix. */
export function FinancingMatrixRowCell(props: FinancingMatrixRowCellProps) {
  const formatCurrency = useFormatCurrency();
  const isCoordinateValid = isValidBalloonCombination(props.downPayment, props.balloonPayment);
  const calculation = props.cell
    ? calculateFinancingOffer({
        pricing: props.pricing,
        term: props.term,
        downPayment: props.downPayment,
        balloonPayment: props.balloonPayment,
        nominalRate: props.cell.nominalRate,
        insurance: {
          included: props.vehicleFinancingBody.insuranceIncludedInCalculation,
          liabilityMonthly: props.vehicleFinancingBody.liabilityInsuranceMonthly,
          comprehensiveMonthly: props.vehicleFinancingBody.comprehensiveInsuranceMonthly,
        },
      })
    : null;
  const isActive =
    props.vehicleFinancingBody.activeTerm === props.term &&
    props.vehicleFinancingBody.activeDownPayment === props.downPayment &&
    props.vehicleFinancingBody.activeBalloonPayment === props.balloonPayment;
  const isCustom =
    props.vehicleFinancingBody.mode === 'MANUAL' ||
    (!props.isTemplatePending &&
      props.cell !== null &&
      (props.templateCell === undefined ||
        !areDecimalValuesEqual(props.templateCell.nominalRate, props.cell.nominalRate) ||
        !areDecimalValuesEqual(props.templateCell.apr, props.cell.apr)));
  const cellState = match({isConfigured: props.cell !== null, isActive, isCustom})
    .with({isConfigured: false}, () => 'EMPTY' as const)
    .with({isConfigured: true, isActive: true}, () => 'ACTIVE' as const)
    .with({isConfigured: true, isActive: false, isCustom: true}, () => 'CUSTOM' as const)
    .with({isConfigured: true, isActive: false, isCustom: false}, () => 'CONFIGURED' as const)
    .exhaustive();
  const coordinates = {
    term: props.term,
    downPayment: props.downPayment,
    balloonPayment: props.type === 'BALLOON' ? props.balloonPayment : null,
  };

  return (
    <Box paddingHorizontal={1} paddingTop={1}>
      <FinancingMatrixCell
        state={cellState}
        primaryValue={isNotNil(props.cell) ? `${props.cell.nominalRate} %` : undefined}
        secondaryValue={
          props.cell
            ? `${props.cell.apr} % ${i18n.t('page.vehicle.financingAndLeasing.apr')}`
            : undefined
        }
        tertiaryValue={
          props.cell // eslint-disable-line no-nested-ternary -- Pre-existing violation
            ? calculation
              ? formatCurrency(calculation.monthlyRate, props.currency)
              : EMPTY_PLACEHOLDER
            : null
        }
        emptyLabel={i18n.t('general.actions.add')}
        ariaLabel={buildMatrixCellAccessibleName({
          action: i18n.t(
            props.cell ? 'page.vehicle.financingAndLeasing.editVariant' : 'general.actions.add'
          ),
          coordinates: [
            {label: i18n.t('page.vehicle.financingAndLeasing.term'), value: props.term},
            {
              label: i18n.t('page.vehicle.financingAndLeasing.downPayment'),
              value: `${props.downPayment} %`,
            },
            {
              label: i18n.t('page.vehicle.financingAndLeasing.balloonPayment'),
              value:
                props.type === 'BALLOON' && isNotNil(props.balloonPayment)
                  ? `${props.balloonPayment} %`
                  : null,
            },
          ],
          statuses: [
            isActive ? i18n.t('page.vehicle.financingAndLeasing.activePrimary') : null,
            isNotNil(props.cell)
              ? i18n.t(
                  isCustom
                    ? 'page.vehicle.financingAndLeasing.manualOverride'
                    : 'page.vehicle.financingAndLeasing.configured'
                )
              : null,
          ],
        })}
        isDisabled={!isCoordinateValid || (props.isReadOnly && isNil(props.cell))}
        isSelected={props.isSelected}
        tooltip={
          isNotNil(props.cell) ? (
            <FinancingCellTooltip
              term={props.term}
              downPayment={props.downPayment}
              balloonPayment={coordinates.balloonPayment}
              vehiclePrice={props.vehiclePrice}
              currency={props.currency}
              nominalRate={props.cell.nominalRate}
              apr={props.cell.apr}
              calculation={calculation}
              isActive={isActive}
              isCustom={isCustom}
            />
          ) : undefined
        }
        onActivate={({isMultiSelect}) => {
          if (isMultiSelect) {
            if (!props.isReadOnly) {
              props.onToggleSelection(coordinates);
            }
            return;
          }

          props.onEditCell(coordinates);
        }}
      />
    </Box>
  );
}
